import {
  clearUserData,
  getAccessToken,
  getRefreshToken,
  setUserData,
} from '@/utils/auth';
// eslint-disable-next-line import/no-cycle
import { refreshJWT } from '@/services/refresh';
import axios, {
  AxiosError,
  AxiosInstance,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
import { redirectToLogin } from '@/helpers/redirectHelper';

const baseUrl = process.env['REACT_APP_BASE_URL'];
const apiVersion = process.env['REACT_APP_API_VERSION'];

if (!baseUrl) {
  throw new Error('REACT_APP_BASE_URL Environment Variable is not defined');
}

if (!apiVersion) {
  throw new Error('REACT_APP_API_VERSION Environment Variable is not defined');
}

export const axiosAuth = axios.create({
  baseURL: `${baseUrl}/${apiVersion}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,
  timeoutErrorMessage: 'No hemos podido cargar datos escenciales',
});

export const apiClient = axios.create({
  baseURL: `${baseUrl}/${apiVersion}`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000,
  timeoutErrorMessage: 'No hemos podido cargar datos esenciales (5 minutos)',
});

const attachAccessToken = (config: InternalAxiosRequestConfig<unknown>) => {
  const token = getAccessToken();
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

apiClient.interceptors.request.use(attachAccessToken, (error) =>
  Promise.reject(error)
);
axiosAuth.interceptors.request.use(attachAccessToken, (error) =>
  Promise.reject(error)
);

// Single-flight refresh: only one refresh request is in flight at a time.
// Every 401 that arrives while a refresh is pending waits for the same promise
// instead of firing its own refresh (which would invalidate the rotating
// refresh token and trigger a cascade of failures).
let refreshPromise: Promise<string> | null = null;

const runRefresh = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = refreshJWT({
      refreshToken: getRefreshToken(),
      accessToken: getAccessToken(),
    })
      .then((response) => {
        setUserData({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
        });
        return response.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

type RetriableConfig = InternalAxiosRequestConfig & {
  retryAttempted?: boolean;
};

const handleAuthError =
  (client: AxiosInstance, publicEndpoints: string[] = []) =>
  (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined;

    if (
      !originalRequest ||
      error.response?.status !== HttpStatusCode.Unauthorized
    ) {
      return Promise.reject(error);
    }

    // Public endpoints handle their own errors, don't try to refresh.
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      originalRequest.url?.includes(endpoint)
    );
    if (isPublicEndpoint) {
      return Promise.reject(error);
    }

    // Already retried once, or there's no refresh token to use: go to login
    // directly instead of hitting the backend with an empty token.
    if (originalRequest.retryAttempted || !getRefreshToken()) {
      clearUserData();
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest.retryAttempted = true;

    return runRefresh()
      .then((accessToken) => {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return client(originalRequest);
      })
      .catch((refreshError) => {
        clearUserData();
        redirectToLogin();
        return Promise.reject(refreshError);
      });
  };

apiClient.interceptors.response.use(
  (response) => response,
  handleAuthError(apiClient)
);

axiosAuth.interceptors.response.use(
  (response) => response,
  handleAuthError(axiosAuth, [
    '/login',
    '/refresh',
    '/sign-up-confirm',
    '/forgot-password',
    '/reset-password',
  ])
);
